/**
 * ProbingDepthControl
 *
 * Per-question follow-up depth shared by text and voice Explore answers.
 *
 * Visually a point-based slider: a horizontal track with 3 dots, the active
 * dot enlarged and filled, and the track filled-in from the leftmost dot to
 * the active one (so it reads as a slider position, not a row of buttons).
 *
 * Interactions supported (any one is sufficient):
 *  - Click a dot - snap to that stop.
 *  - Click the bar between dots - snap to the nearest stop.
 *  - Drag along the bar - pointer capture keeps the gesture alive even if the
 *    cursor leaves the track; `step=1` snapping happens on every move event.
 *  - Keyboard arrows - Headless UI's `RadioGroup` wires Left/Right.
 *
 * The Beta badge is NOT baked in - consumers pass one via `labelSuffix` so
 * the chrome stays in sync with sibling controls (the same pattern Toggle
 * uses).
 *
 * @module components/ui/ProbingDepthControl
 */

import { AnimatedLabel } from './AnimatedLabel';
import React, { useCallback, useRef } from 'react';
import { Radio, RadioGroup } from '@headlessui/react';

/** A single depth stop on the slider. */
interface DepthOption {
  /** Numeric depth value (2 = Light, 4 = Deep). */
  value: number;
  /** Short label rendered under the dot. */
  label: string;
}

/** Static stop list - order is significant (left -> right). */
const DEPTH_OPTIONS: ReadonlyArray<DepthOption> = [
  { value: 2, label: 'Light' },
  { value: 3, label: 'Balanced' },
  { value: 4, label: 'Deep' },
];

/**
 * Per-stop caption shown under the slider. Switches as the user drags so the
 * label always describes the *current* level rather than a static "what this
 * control is" sentence - admins want to know what they just selected.
 */
const DEPTH_CAPTIONS: Record<number, string> = {
  2: 'One purposeful follow-up.',
  3: 'Up to two purposeful follow-ups.',
  4: 'Up to five purposeful follow-ups.',
};

/** Props for {@link ProbingDepthControl}. */
export interface ProbingDepthControlProps {
  /** Current Explore depth (2-4). Legacy 1 is displayed as Light. */
  value: number;
  /** Called with the new depth when the admin picks a stop. */
  onChange: (v: number) => void;
  /** Heading shown above the slider. Defaults to "Probing depth". */
  label?: string;
  /**
   * One-line caption rendered under the slider. When omitted, the caption
   * text follows the active stop via {@link DEPTH_CAPTIONS}.
   */
  description?: string;
  /**
   * Optional node rendered inline after the label (e.g. a `<Beta />` chip).
   * Mirrors the `labelSuffix` pattern from `Toggle`.
   */
  labelSuffix?: React.ReactNode;
  /** When true, the slider is dimmed and clicks/keys/drag are ignored. */
  disabled?: boolean;
}

/**
 * "Beta" chip for use as a `labelSuffix`.
 *
 * Matches the platform Beta mark (the volt-wash chip beside the holito logo):
 * a flat volt-wash fill, not the gated yellow used by `FeatureGateBadge`, so
 * admins don't read it as a paywall. This control is fully usable on every
 * plan; the badge just signals that the levels and behaviors may shift while
 * we tune.
 */
export const Beta: React.FC = () => (
  <span className="inline-block px-1.5 py-0.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-wash-accent text-foreground">
    Beta
  </span>
);

/**
 * 3-stop probing-depth slider. Renders a label row, a horizontal track with
 * dots at each stop, labels under each dot, and a one-line caption.
 */
export const ProbingDepthControl: React.FC<ProbingDepthControlProps> = ({
  value,
  onChange,
  label = 'Probing depth',
  description,
  labelSuffix,
  disabled = false,
}) => {
  // Explore always includes a follow-up. Old Surface values display as Light.
  // Clamp invalid numbers so the fill cannot extend outside the track.
  const safeValue = Number.isFinite(value)
    ? Math.max(2, Math.min(4, Math.round(value)))
    : 3;
  // 2 -> 0%, 3 -> 50%, 4 -> 100%. The fill stretches from the
  // leftmost dot to the active dot so the slider reads as a position, not a
  // grouping of equal segments.
  const fillPercent = ((safeValue - 2) / 2) * 100;
  // Caption defaults to the per-stop description so admins can read what the
  // current level does. Consumers can pin a static string by passing
  // `description` explicitly.
  const captionText = description ?? DEPTH_CAPTIONS[safeValue];

  // Drag state. The pointer-capture pattern means we don't have to attach
  // window-level listeners - the track wrapper itself keeps receiving move
  // events even when the cursor strays outside it.
  const trackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  /**
   * Map a pointer's clientX to the nearest stop value (2..4). Uses the track
   * wrapper's bounding rect so the math accounts for any horizontal padding.
   */
  const valueAtClientX = useCallback((clientX: number): number => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return safeValue;
    const ratio = (clientX - rect.left) / rect.width;
    const clamped = Math.max(0, Math.min(1, ratio));
    return Math.round(clamped * 2) + 2;
  }, [safeValue]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) return;
      isDraggingRef.current = true;
      // Pointer capture lets the gesture survive the cursor leaving the
      // track. Wrapped because jsdom (test environment) doesn't implement
      // the API and would otherwise throw.
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Capture not supported here - drag still works for cursor moves
        // that stay over the track.
      }
      const next = valueAtClientX(e.clientX);
      if (next !== safeValue) onChange(next);
    },
    [disabled, onChange, safeValue, valueAtClientX],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current || disabled) return;
      const next = valueAtClientX(e.clientX);
      if (next !== safeValue) onChange(next);
    },
    [disabled, onChange, safeValue, valueAtClientX],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Pointer was already released by the browser; safe to ignore.
      }
    },
    [],
  );

  return (
    <div data-testid="probing-depth-control">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <h3 className="text-volt font-bold uppercase tracking-wider text-xs">
          {label}
        </h3>
        {labelSuffix}
      </div>

      <RadioGroup
        value={safeValue}
        onChange={onChange}
        disabled={disabled}
        aria-label={label}
        className={disabled ? 'opacity-60 cursor-not-allowed' : ''}
      >
        {/* Track + dots row. Pointer handlers on the wrapper provide drag;
            Headless UI handles per-dot click + keyboard nav. */}
        <div
          ref={trackRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className={[
            'relative h-6 flex items-center select-none touch-none',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}
        >
          {/* Inactive track - full width. The horizontal padding (`mx-2.5`)
              aligns the track endpoints with the centers of the leftmost and
              rightmost dots so the line doesn't poke past them. */}
          <div className="absolute inset-x-0 mx-2.5 top-1/2 -translate-y-1/2 h-1 bg-surface-raised rounded-full" />
          {/* Active fill - stretches from the leftmost dot to the active one. */}
          <div
            className="absolute left-0 ml-2.5 top-1/2 -translate-y-1/2 h-1 bg-volt rounded-full transition-all duration-150 motion-reduce:transition-none"
            style={{ width: `calc((100% - 1.25rem) * ${fillPercent / 100})` }}
          />

          {/* Dots - one per stop. Tri-state styling so the slider visibly
              accumulates as the admin moves right:
                past   (opt.value <  active) - small blue dot
                active (opt.value === active) - larger blue dot with halo
                future (opt.value >  active) - small grey dot
              Headless UI handles keyboard nav + focus on the same element. */}
          <div className="relative flex w-full justify-between">
            {DEPTH_OPTIONS.map((opt) => {
              const isPast = opt.value < safeValue;
              const isActive = opt.value === safeValue;
              const isFilled = isPast || isActive;
              return (
                <Radio
                  key={opt.value}
                  value={opt.value}
                  aria-label={opt.label}
                  data-testid={`probing-depth-option-${opt.value}`}
                  className={({ checked }) =>
                    [
                      'group relative flex h-5 w-5 items-center justify-center rounded-full transition-all motion-reduce:transition-none',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-volt focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                      checked ? 'border-volt bg-volt/10' : '',
                    ].join(' ')
                  }
                >
                  <span
                    aria-hidden
                    className={[
                      'block rounded-full transition-all duration-150 motion-reduce:transition-none',
                      isActive
                        ? 'h-4 w-4 bg-volt ring-4 ring-volt/20'
                        : isFilled
                          ? 'h-2.5 w-2.5 bg-volt'
                          : 'h-2.5 w-2.5 bg-surface-raised group-hover:bg-muted',
                    ].join(' ')}
                  />
                </Radio>
              );
            })}
          </div>
        </div>

        {/* Labels row, aligned with each dot. */}
        <div className="flex w-full justify-between mt-2 text-xs">
          {DEPTH_OPTIONS.map((opt) => {
            const isActive = safeValue === opt.value;
            return (
              <span
                key={opt.value}
                className={
                  isActive
                    ? 'text-foreground font-semibold'
                    : 'text-muted-foreground'
                }
              >
                {opt.label}
              </span>
            );
          })}
        </div>
      </RadioGroup>

      <p className="text-xs text-subtle-foreground mt-2"><AnimatedLabel text={captionText} multiline /></p>
    </div>
  );
};
